import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X, Camera } from "lucide-react";

const READER_ID = "qkiere-qr-reader";

// Html5Qrcode.stop()/.clear() are plain (non-async) functions: when called in
// the wrong state they throw synchronously instead of rejecting a promise, so
// a bare .catch() never sees it. Wrap every call so a double-stop (decode
// success + the modal unmounting right after) can never crash the page.
function safeClear(scanner) {
  try { scanner.clear(); } catch { /* already cleared / never started */ }
}

function safeStop(scanner) {
  try {
    scanner.stop().then(() => safeClear(scanner)).catch(() => safeClear(scanner));
  } catch {
    safeClear(scanner);
  }
}

// Modal que abre la cámara y escanea un QR — pensado para leer el QR que el cliente
// ya tiene en "Mi cuenta" (codifica su id crudo), pero decodifica cualquier texto;
// quien llama decide qué hacer con el resultado.
export default function QrScannerModal({ onScan, onClose }) {
  const scannerRef = useRef(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    let stopped = false;
    const scanner = new Html5Qrcode(READER_ID);
    scannerRef.current = scanner;

    scanner.start(
      { facingMode: "environment" },
      { fps: 10, qrbox: 230 },
      (decodedText) => {
        if (stopped) return;
        stopped = true;
        safeStop(scanner);
        onScan(decodedText);
      },
      () => {} // fallo de un frame individual — normal, se ignora
    )
      .then(() => setStarting(false))
      .catch(() => {
        setStarting(false);
        setError("No se pudo acceder a la cámara. Revisa los permisos del navegador.");
      });

    return () => {
      stopped = true;
      safeStop(scanner);
    };
  }, [onScan]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-800 flex items-center gap-2">
            <Camera size={16} className="text-supernova-600" /> Escanear QR del cliente
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>

        {error ? (
          <p className="text-sm text-red-500 py-6 text-center">{error}</p>
        ) : (
          <>
            <div id={READER_ID} className="rounded-xl overflow-hidden bg-black" />
            {starting && <p className="text-xs text-gray-400 text-center mt-2">Iniciando cámara…</p>}
            <p className="text-xs text-gray-400 text-center mt-2">Apunta al QR de "Mi cuenta" del cliente</p>
          </>
        )}
      </div>
    </div>
  );
}
